import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useCountryApi custom hook for country API operations.
 * - Provides CRUD operations for countries using Apollo Client and GraphQL.
 * - Handles cache updates for country queries and mutations.
 * - Exposes helpers for reading/writing/updating countries in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting countries.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const COUNTRY_QUERIES = {
  // Query to fetch all countries
  GET_COUNTRIES: gql`
    query GetCountries {
      GetCountries {
        country_id
        country_name
        country_code
        short_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a country by ID
  GET_COUNTRY: gql`
    query GetCountryById($country_id: ID!) {
      GetCountryById(country_id: $country_id) {
        country_id
        country_name
        country_code
        short_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new country
  ADD_COUNTRY: gql`
    mutation AddCountry(
      $country_name: String!
      $country_code: String!
      $short_name: String!
    ) {
      AddCountry(
        country_name: $country_name
        country_code: $country_code
        short_name: $short_name
      ) {
        country_id
        country_name
        country_code
        short_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing country
  UPDATE_COUNTRY: gql`
    mutation UpdateCountry(
      $country_id: ID!
      $country_name: String
      $country_code: String
      $short_name: String
      $is_active: Boolean
    ) {
      UpdateCountry(
        country_id: $country_id
        country_name: $country_name
        country_code: $country_code
        short_name: $short_name
        is_active: $is_active
      ) {
        country_id
        country_name
        country_code
        short_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a country
  DELETE_COUNTRY: gql`
    mutation DeleteCountry($country_id: ID!) {
      DeleteCountry(country_id: $country_id) {
        country_id
        country_name
        is_active
      }
    }
  `,
};

/**
 * Cache operations for countries.
 * - readCountries: Reads countries from Apollo cache.
 * - writeCountries: Writes countries to Apollo cache.
 * - updateCountryInCache: Updates a country in Apollo cache.
 */
const cacheOperations = {
  readCountries: () => {
    try {
      const { GetCountries = [] } =
        client.readQuery({
          query: COUNTRY_QUERIES.GET_COUNTRIES,
        }) || {};
      return GetCountries;
    } catch {
      return [];
    }
  },

  writeCountries: (countries) => {
    if (!countries) return;
    client.writeQuery({
      query: COUNTRY_QUERIES.GET_COUNTRIES,
      data: { GetCountries: countries },
    });
  },

  updateCountryInCache: (updatedCountry) => {
    const existingCountries = cacheOperations.readCountries();
    const updatedCountries = existingCountries.map((country) =>
      country.country_id === updatedCountry.country_id
        ? updatedCountry
        : country
    );
    cacheOperations.writeCountries(updatedCountries);
  },
};

export const useCountryApi = () => {
  // Mutation for adding a country
  const [addCountryMutation] = useMutation(COUNTRY_QUERIES.ADD_COUNTRY, {
    update(cache, { data: { AddCountry } }) {
      try {
        const existingData = cache.readQuery({
          query: COUNTRY_QUERIES.GET_COUNTRIES,
        });
        if (existingData) {
          cache.writeQuery({
            query: COUNTRY_QUERIES.GET_COUNTRIES,
            data: {
              GetCountries: [AddCountry, ...existingData.GetCountries],
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after adding country:", error);
      }
    },
  });

  // Mutation for updating a country
  const [updateCountryMutation] = useMutation(COUNTRY_QUERIES.UPDATE_COUNTRY, {
    update(cache, { data: { UpdateCountry } }) {
      try {
        const existingData = cache.readQuery({
          query: COUNTRY_QUERIES.GET_COUNTRIES,
        });
        if (existingData) {
          const newData = existingData.GetCountries.map((country) =>
            country.country_id === UpdateCountry.country_id ? UpdateCountry : country
          );
          cache.writeQuery({
            query: COUNTRY_QUERIES.GET_COUNTRIES,
            data: { GetCountries: newData },
          });
        }
      } catch (error) {
        console.error("Error updating cache after updating country:", error);
      }
    },
  });

  // Mutation for deleting a country
  const [deleteCountryMutation] = useMutation(COUNTRY_QUERIES.DELETE_COUNTRY, {
    update(cache, { data: { DeleteCountry } }) {
      try {
        const existingData = cache.readQuery({
          query: COUNTRY_QUERIES.GET_COUNTRIES,
        });
        if (existingData) {
          cache.writeQuery({
            query: COUNTRY_QUERIES.GET_COUNTRIES,
            data: {
              GetCountries: existingData.GetCountries.filter(
                (country) => country.country_id !== DeleteCountry.country_id
              ),
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after deleting country:", error);
      }
    },
  });

  /**
   * Fetches all countries from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getCountries = async () => {
    try {
      const { data } = await client.query({
        query: COUNTRY_QUERIES.GET_COUNTRIES,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      return { success: true, data: data.GetCountries };
    } catch (error) {
      console.error("Error fetching countries:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a country by its ID.
   * @param {string|number} country_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getCountryById = async (country_id) => {
    try {
      const { data } = await client.query({
        query: COUNTRY_QUERIES.GET_COUNTRY,
        variables: { country_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetCountryById };
    } catch (error) {
      console.error("Error fetching country:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new country.
   * @param {Object} formData - { country_name, country_code, short_name, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createCountry = async (formData) => {
    try {
      const { data } = await addCountryMutation({
        variables: {
          country_name: formData.country_name,
          country_code: formData.country_code,
          short_name: formData.short_name,
          is_active: formData.is_active
        },
      });
      return { success: true, data: data.AddCountry };
    } catch (error) {
      console.error("Error creating country:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing country.
   * @param {string|number} countryId
   * @param {Object} formData - { country_name, country_code, short_name, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateCountry = async (countryId, formData) => {
    try {
      const { data } = await updateCountryMutation({
        variables: {
          country_id: countryId,
          country_name: formData.country_name,
          country_code: formData.country_code,
          short_name: formData.short_name,
          is_active: formData.is_active
        },
      });
      return { success: true, data: data.UpdateCountry };
    } catch (error) {
      console.error("Error updating country:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a country by its ID.
   * @param {string|number} country_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteCountry = async (country_id) => {
    try {
      const { data } = await deleteCountryMutation({
        variables: { country_id },
      });
      return { success: true, data: data.DeleteCountry };
    } catch (error) {
      console.error("Error deleting country:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getCountries,
    getCountryById,
    createCountry,
    updateCountry,
    deleteCountry,
    cacheOperations,
  };
};
